import { unified, type Processor } from 'unified';
import remarkParse from 'remark-parse';
import remarkGfm from 'remark-gfm';
import remarkFrontmatter from 'remark-frontmatter';
import remarkMath from 'remark-math';
import { remarkAlert } from 'remark-github-blockquote-alert';
import remarkRehype from 'remark-rehype';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import rehypeHighlight from 'rehype-highlight';
import rehypeStringify from 'rehype-stringify';
import { sanitizeSchema } from './sanitize-schema.js';
import { rehypeMermaidSource } from './rehype-mermaid-source.js';
import { rehypeQuerySource } from './rehype-query-source.js';
import { rehypeSourceLines } from './rehype-source-lines.js';
import { rehypeTaskListLabels } from './rehype-task-list-labels.js';

/**
 * The single shared markdown pipeline.
 *
 * The SAME processor feeds the live preview AND every export path, so what you
 * see is exactly what you export. Ordering matters:
 *
 *   parse → frontmatter/gfm/math/alerts (mdast)
 *   → rehype (+ raw HTML) → SANITIZE untrusted input
 *   → mermaid-source / highlight  (trusted transforms, post-sanitize)
 *   → stringify
 *
 * Everything before sanitize is treated as hostile; everything after is our own
 * trusted output. See `sanitize-schema.ts` for the rationale.
 *
 * Math is intentionally NOT rendered here: remark-math emits
 * `<span class="math-inline">…</span>` / `<div class="math-display">…</div>`
 * carrying the raw TeX, and the client renders them lazily with KaTeX (like
 * mermaid) so the ~275KB KaTeX bundle only loads for documents that contain
 * math. Exporters run the same client-side KaTeX pass before serializing.
 */
function createProcessor(): Processor {
  return (
    unified()
      .use(remarkParse)
      // Frontmatter is metadata, not prose: parsing it here keeps a `---` block at
      // the top of a note out of the preview and every export, instead of showing
      // up as a horizontal rule followed by stray `key: value` text.
      .use(remarkFrontmatter, ['yaml'])
      .use(remarkGfm)
      .use(remarkMath)
      .use(remarkAlert)
      .use(remarkRehype, { allowDangerousHtml: true })
      .use(rehypeSourceLines)
      .use(rehypeRaw)
      .use(rehypeSanitize, sanitizeSchema)
      .use(rehypeMermaidSource)
      .use(rehypeQuerySource)
      .use(rehypeTaskListLabels)
      .use(rehypeHighlight, { detect: true, ignoreMissing: true })
      .use(rehypeStringify) as unknown as Processor
  );
}

let cached: Processor | null = null;

function processor(): Processor {
  if (!cached) cached = createProcessor().freeze();
  return cached;
}

/**
 * Render markdown to sanitized HTML.
 *
 * The result is safe to inject via `dangerouslySetInnerHTML` (preview) or to
 * embed in an exported document — no scripts, no event handlers survive.
 */
export function render(markdown: string): string {
  const file = processor().processSync(markdown);
  return String(file);
}

export { sanitizeSchema } from './sanitize-schema.js';
export {
  buildStandaloneHtml,
  escapeHtml,
  themeCss,
  type StandaloneHtmlOptions,
} from './export/index.js';
